import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import NoImage from "../../../src/assets/images/image_not_found.webp";

export interface OutNotificationItem {
  id: number;
  title: string;
  content: string;
  type: string;
  trigger?: string;

  category?: string | null;
  created?: string;

  desktopImage?: string | File | null;
  mobileImage?: string | File | null;

  isActive?: number;

  targetType?: string;
  targetId?: number;
  targetTitle?: string;
  targetSlug?: string | null;
  buttonTextKey?: string;
  removeAfterHours?: number | string | null;
}
export interface NotifBunchPayload {
  ids: number[];
  isActive: number;
}

export interface NotificationTranslation {
  locale: string;
  title: string;
  content: string;
  isActive: number;
}

interface OutNotificationsState {
  list: OutNotificationItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  payload?: OutNotificationItem;
  isItemFetching: boolean;
  processingItemId?: OutNotificationItem["id"];
  categories: string[];
  triggers: string[];

  targetTypes: {
    value: string;
    title: string;
  }[];

  ctaTargets: {
    label: string;
    value: number;
    slug?: string | null;
  }[];

  isCtaLoading: boolean;
  placeholders: string[];
  isPlaceholdersLoading: boolean;
}
interface GetListPayload {
  limit: number;
  offset: number;
  type: string;
  title: string;
  isActive?: number;
  category?: string[];
  trigger?: string[];
  order: {
    title?: string;
    isActive?: string;
  };
}

function appendRemoveAfterHours(
  data: FormData,
  value: OutNotificationItem["removeAfterHours"],
) {
  if (value === undefined || value === null || String(value).trim() === "") {
    data.append("removeAfterHours", "");
    return;
  }
  data.append("removeAfterHours", String(value).trim());
}

const initialState: OutNotificationsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  // @ts-ignore
  payload: {
    type: "manual",
  },
  isItemFetching: false,
  categories: [],
  triggers: [],

  targetTypes: [],
  ctaTargets: [],
  isCtaLoading: false,
  placeholders: [],
  isPlaceholdersLoading: false,
};

export const getNotificationsListList = createAsyncThunk(
  "out-notif/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.notificationsList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getNotificationItem = createAsyncThunk(
  "out-notif/item",
  async (id: string) => {
    try {
      const result = await instance.get(`${apiRoutes.notificationItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createNotification = createAsyncThunk(
  "out-notif/create",
  async (payload: OutNotificationItem, { rejectWithValue }) => {
    try {
      const data = new FormData();

      if (payload.desktopImage instanceof File) {
        const safeName = sanitizeFileName(payload.desktopImage.name);

        data.append(
          "desktopImage",
          new File([payload.desktopImage], safeName, {
            type: payload.desktopImage.type,
          }),
        );
      }

      if (payload.mobileImage instanceof File) {
        const safeName = sanitizeFileName(payload.mobileImage.name);

        data.append(
          "mobileImage",
          new File([payload.mobileImage], safeName, {
            type: payload.mobileImage.type,
          }),
        );
      }

      data.append("title", payload.title ?? "");
      data.append("content", payload.content ?? "");
      data.append("type", payload.type ?? "");

      if (payload.category) {
        data.append("category", payload.category ?? "");
      }

      if (payload.trigger) {
        data.append("trigger", payload.trigger ?? "");
      }

      data.append("isActive", payload.isActive ? "1" : "0");
      if (payload.buttonTextKey) {
        data.append("buttonTextKey", payload.buttonTextKey);
      }

      if (payload.targetType) {
        data.append("targetType", payload.targetType);
      }

      if (payload.targetId) {
        data.append("targetId", String(payload.targetId));
      }

      if (payload.targetTitle) {
        data.append("targetTitle", payload.targetTitle);
      }

      if (payload.targetSlug) {
        data.append("targetSlug", payload.targetSlug);
      }

      appendRemoveAfterHours(data, payload.removeAfterHours);

      const result = await instance.post(apiRoutes.notificationCreate, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return result.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateNotification = createAsyncThunk(
  "out-notif/update",
  async (payload: OutNotificationItem, { rejectWithValue }) => {
    try {
      const data = new FormData();

      if (payload.desktopImage instanceof File) {
        const safeName = sanitizeFileName(payload.desktopImage.name);

        data.append(
          "desktopImage",
          new File([payload.desktopImage], safeName, {
            type: payload.desktopImage.type,
          }),
        );
      }

      if (payload.mobileImage instanceof File) {
        const safeName = sanitizeFileName(payload.mobileImage.name);

        data.append(
          "mobileImage",
          new File([payload.mobileImage], safeName, {
            type: payload.mobileImage.type,
          }),
        );
      }

      data.append("title", payload.title ?? "");
      data.append("content", payload.content ?? "");
      data.append("type", payload.type);

      if (payload.category) {
        data.append("category", payload.category ?? "");
      }

      if (payload.trigger) {
        data.append("trigger", payload.trigger ?? "");
      }

      data.append("isActive", payload.isActive ? "1" : "0");
      if (payload.buttonTextKey) {
        data.append("buttonTextKey", payload.buttonTextKey);
      }

      if (payload.targetType) {
        data.append("targetType", payload.targetType);
      }

      if (payload.targetId) {
        data.append("targetId", String(payload.targetId));
      }

      if (payload.targetTitle) {
        data.append("targetTitle", payload.targetTitle);
      }

      if (payload.targetSlug) {
        data.append("targetSlug", payload.targetSlug);
      }

      appendRemoveAfterHours(data, payload.removeAfterHours);

      await instance.put(
        `${apiRoutes.notificationUpdate}/${payload.id}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateNotifBunch = createAsyncThunk(
  "out-notif/update-bunch",
  async (data: NotifBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.notificationUpdateBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const deleteNotification = createAsyncThunk(
  "out-notif/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.notificationDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getNotificationCategories = createAsyncThunk(
  "out-notif/categories",
  async () => {
    try {
      const result = await instance.get(apiRoutes.notificationCategories);

      return result.data;
    } catch (error) {
      console.log(error);

      return undefined;
    }
  },
);

export const getNotificationTargetTypes = createAsyncThunk(
  "out-notif/target-types",
  async () => {
    try {
      const result = await instance.get(apiRoutes.notificationTargetTypes);

      return result.data;
    } catch (error) {
      console.log(error);

      return undefined;
    }
  },
);

export const getNotificationPlaceholders = createAsyncThunk(
  "out-notif/placeholders",
  async (category?: string) => {
    try {
      const result = await instance.get(
        category
          ? `${apiRoutes.notificationPlaceholders}?category=${category}`
          : apiRoutes.notificationPlaceholders,
      );

      return result.data;
    } catch (error) {
      console.log(error);

      return undefined;
    }
  },
);

export const getNotificationTargetEntities = createAsyncThunk(
  "out-notif/get-target-entities",
  async ({ targetType, search }: { targetType: string; search?: string }) => {
    let response;

    switch (targetType) {
      case "bonus":
        response = await instance.post(apiRoutes.bonusesList, {
          title: search ?? undefined,
          limit: 100,
          offset: 0,
        });
        break;

      case "game":
        response = await instance.post(apiRoutes.gamesList, {
          gameTitle: search ?? undefined,
          limit: 100,
          offset: 0,
          isActive: 1,
        });
        break;

      case "collection":
        response = await instance.post(apiRoutes.collectionsList, {
          title: search ?? undefined,
          limit: 100,
          offset: 0,
          isActive: 1,
        });
        break;

      case "tag":
        response = await instance.post(apiRoutes.tagsList, {
          title: search ?? undefined,
          limit: 100,
          offset: 0,
          isActive: 1,
        });
        break;

      default:
        return [];
    }

    return response.data?.items ?? response.data?.data?.items ?? [];
  },
);

export const getNotificationAutoTriggers = createAsyncThunk(
  "out-notif/triggers",
  async () => {
    try {
      const result = await instance.get(apiRoutes.notificationAutoTriggers);

      return result.data;
    } catch (error) {
      console.log(error);

      return undefined;
    }
  },
);

export const outNotifSlice = createSlice({
  name: "out-notif",
  initialState,
  reducers: {
    handleClearFields: (state: OutNotificationsState) => {
      // @ts-ignore
      state.payload = {
        type: "manual",
      };
      state.isItemFetched = false;
    },
    handleUpdateFields: (
      state: OutNotificationsState,
      action: PayloadAction<OutNotificationItem>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handlePaginate: (
      state: OutNotificationsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (
      state: OutNotificationsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: OutNotificationsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotificationsListList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getNotificationItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = {
        ...action.payload,
        isActive: action.payload?.isActive ? 1 : 0,
      };
    });
    builder.addCase(createNotification.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createNotification.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createNotification.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(deleteNotification.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || undefined;
    });
    builder.addCase(deleteNotification.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteNotification.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updateNotifBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(updateNotification.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateNotification.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              desktopImage:
                action.meta.arg.desktopImage instanceof File
                  ? item.desktopImage
                  : action.meta.arg.desktopImage,
              mobileImage:
                action.meta.arg.mobileImage instanceof File
                  ? item.mobileImage
                  : action.meta.arg.mobileImage,
            })
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateNotification.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getNotificationCategories.fulfilled, (state, action) => {
      state.categories = action.payload?.items || [];
    });

    builder.addCase(getNotificationTargetTypes.fulfilled, (state, action) => {
      state.targetTypes = action.payload?.items || [];
    });
    builder.addCase(getNotificationPlaceholders.pending, (state) => {
      state.isPlaceholdersLoading = true;
    });

    builder.addCase(getNotificationPlaceholders.fulfilled, (state, action) => {
      state.isPlaceholdersLoading = false;
      state.placeholders = action.payload?.items || [];
    });

    builder.addCase(getNotificationPlaceholders.rejected, (state) => {
      state.isPlaceholdersLoading = false;
      state.placeholders = [];
    });

    builder.addCase(getNotificationAutoTriggers.fulfilled, (state, action) => {
      state.triggers = action.payload?.items || [];
    });
    builder.addCase(getNotificationTargetEntities.pending, (state) => {
      state.isCtaLoading = true;
    });

    builder.addCase(
      getNotificationTargetEntities.fulfilled,
      (state, action) => {
        state.isCtaLoading = false;

        state.ctaTargets = action.payload.map((item: any) => ({
          label: item.title || item.name,
          value: item.id,
          slug: item.slug,
          img: item.imageSmall || item.image || NoImage,
        }));
      },
    );

    builder.addCase(getNotificationTargetEntities.rejected, (state) => {
      state.isCtaLoading = false;
      state.ctaTargets = [];
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleClearFields,
  handleUpdateFields,
} = outNotifSlice.actions;
